import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button, Container, Row, Col, Alert, ProgressBar } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import CardList from './CardList';
import CardModal from './CardModal';
import CardPicker from './CardPicker';
import { useUser } from '../UserContext';
import { buildUrlWithParams } from './utils.js';
import ContestPhaseIndicator from './ContestPhaseIndicator';
import './Contest.css';

// Utility function to determine contest phase
const getContestPhase = (startDate, submissionsEndDate, endDate) => {
  const currentTime = Math.floor(Date.now() / 1000); // Current time in Unix timestamp

  if (currentTime < startDate) {
    return 'not_started';
  } else if (currentTime >= startDate && currentTime < submissionsEndDate) {
    return 'submission';
  } else if (currentTime >= submissionsEndDate && currentTime < endDate) {
    return 'voting';
  } else {
    return 'cooldown';
  }

};

const memeMessages = [
  "Sorry, but this contest is in another castle.",
  "These aren't the contests you're looking for.",
  "An Illusion what are you hiding?",
  "It's a Secret to Everybody.",
  "Are You Winning, Son?",
  "Contest isn’t ready yet? You’re not thinking fourth-dimensionally!",
  "This is fine.",
  "This is the way."
];

const randomMessage = memeMessages[Math.floor(Math.random() * memeMessages.length)];

const Contest = () => {
  const { id } = useParams();
  const [contestInfo, setContestInfo] = useState(null);
  const [userSubmission, setUserSubmission] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [showCardPicker, setShowCardPicker] = useState(false);
  const [currentPhase, setCurrentPhase] = useState(null);
  const [themeCards, setThemeCards] = useState([]);

  const { user, setUser, selectedCard, setSelectedCard, settings, setSettings, cards, setCards } = useUser();

  useEffect(() => {
    fetchContestInfo();
    fetchSubmissions();
    if (user) {
      fetchUserSubmission();
    } else {
      setUserSubmission(null);
    }
  }, [user, id]);

  // Update phase every minute
  useEffect(() => {
    if (contestInfo) {
      const updatePhase = () => {
        const phase = getContestPhase(
          contestInfo.start_date,
          contestInfo.submissions_end_date,
          contestInfo.end_date
        );
        setCurrentPhase(phase);
      };

      updatePhase(); // Initial phase set
      const interval = setInterval(updatePhase, 1000); // Update every minute

      return () => clearInterval(interval);
    }
  }, [contestInfo]);

  // Fetch theme cards only when in voting phase
  useEffect(() => {
    if (currentPhase === 'voting' || currentPhase === 'cooldown') {
      fetchThemeCards();
    }
  }, [contestInfo, currentPhase]);

  const fetchContestInfo = async () => {
    try {
      const response = await axios.get(`/api/contest/get_contest_info.php?id=${id}`);
      setContestInfo(response.data);
      // Set initial phase
      setCurrentPhase(getContestPhase(
        response.data.start_date,
        response.data.submissions_end_date,
        response.data.end_date
      ));
    } catch (error) {
      console.error('Error fetching contest info:', error);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`/api/contest/get_submissions.php?id=${id}`);
      //console.log(response.data);
      setSubmissions(response.data);
      const updatedData = response.data.map(submission => ({
        ...submission,
        id: String(submission.id), // Ensure id is a string
      }));
      setCards(updatedData);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  const fetchUserSubmission = async () => {
    try {
      const response = await axios.get(`/api/contest/user-submission.php?contest_id=${id}&user_id=${user.userid}`);
      setUserSubmission(response.data);
    } catch (error) {
      console.error('Error fetching user submission:', error);
    }
  };

  const fetchThemeCards = async () => {
    try {
      const response = await axios.get(`/api/contest/get_theme_cards.php?id=${id}`);
      // The response should return an array of theme objects, e.g.,
      // [{ id: 1, name: 'Nature', image: 'nature.webp', votes: 10, votePercentage: 40 }, ...]
      setThemeCards(response.data);
    } catch (error) {
      console.error('Error fetching theme cards:', error);
    }
  };

  const handleWithdraw = async () => {
    try {
      await axios.post(`/api/contest/withdraw-card.php`, { contestId: id, userId: user.userid });
      setUserSubmission(null);
      fetchSubmissions();
    } catch (error) {
      console.error('Error withdrawing card:', error);
    }
  };

  const handleSubmit = async (cardId) => {
    try {
      console.log(id);
      await axios.post(`/api/contest/submit-card.php`, { contestId: id, userId: user.userid, cardId: cardId });
      fetchUserSubmission();
      fetchSubmissions();
    } catch (error) {
      console.error('Error submitting card:', error);
    }
  };

  const handleVote = async (cardId) => {
    try {
      await axios.post(`/api/contest/vote/${id}`, { userId: user.userid, cardId });
      fetchSubmissions();
    } catch (error) {
      console.error('Error voting for card:', error);
    }
  };

  const handleThemeVote = async (themeId) => {
    try {
      await axios.post(`/api/contest/vote_theme.php`, { contestId: id, themeId: themeId });
      // Optionally, refresh the theme cards after voting to update vote counts
      fetchThemeCards();
    } catch (error) {
      console.error('Error voting for theme:', error);
    }
  };

  const renderSubmissionContent = () => {
    if (!user) {
      return (
        <Col lg={5} className="d-flex align-items-center justify-content-center">
          <div className="text-center">
            <p className="lead">
              To submit a card, please log in.
            </p>
          </div>
        </Col>
      );
    }

    // Only allow submissions during submission phase
    if (currentPhase !== 'submission') {
      return (
        <Col lg={5} className="d-flex align-items-center justify-content-center">
          <div className="text-center">
            <p className="lead">
              The submission phase has ended.
            </p>
          </div>
        </Col>
      );
    }

    return (
      <Col lg={5} className="d-flex align-items-center justify-content-center">
        {userSubmission ? (
          <div className="text-center">
            <Link
              to={buildUrlWithParams({ ...Object.fromEntries(new URLSearchParams(window.location.search)), selectedCard: userSubmission.cardId })}
              preventScrollReset={true}
              style={{ textDecoration: 'none', zIndex: '2' }}
            >
              <img
                src={`/cards/${userSubmission.cardId2}.webp`}
                alt="Your submission"
                className="img-fluid mb-3 submission-card"
                style={{ maxWidth: '300px' }}
              />
            </Link>
            <div>
              <Button variant="danger" onClick={handleWithdraw}>
                Withdraw Submission
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="primary"
            onClick={() => setShowCardPicker(true)}
            className="contest-submit-button"
          >
            <p className="fs-1 mb-0">+</p>
            <p className="mt-0">Submit a Card</p>
          </Button>
        )}
      </Col>
    );
  };


  const renderPhaseContent = () => {
    switch (currentPhase) {
      case 'not_started':
        return (
          <div className="text-center">
            <h2 className="mb-4">Contest Not Started</h2>
            <Alert variant="info" className="mx-5 filter">
              <p className="mb-0">
                This contest will begin on {new Date(contestInfo.start_date * 1000).toLocaleString()}
              </p>
            </Alert>
          </div>
        );
      case 'submission':
        return (
          <>
            <Col lg={10}>
              <Row className="pt-lg-3 mb-4">
                <Col lg={7} className="mb-4 mb-lg-0">
                  <h3 className="mb-3">Submission Rules</h3>
                  <ul className="lead text-start">
                    <li>You may submit <strong>one</strong> card.</li>
                    <li>If the card has <strong>tokens</strong>, they should be <strong>attached to the card</strong>.</li>
                    <li>You can <strong>attach tokens or edit the card by clicking on it</strong> after submitting it.</li>
                    <li>During the submission phase, you'll be able to edit or completely change your submission freely.</li>
                    <li>Once the Voting Phase begins, your submission will be locked.</li>
                    <li>After the Voting Phase, card with the most likes will be declared as the Winner.</li>
                  </ul>
                </Col>
                {renderSubmissionContent()}
              </Row>

              {/* <hr className="my-5" style={{ borderWidth: '3px' }} /> */}
              {/* <img src="https://www.hearthcards.net/images/contest/separator.webp" className="img-fluid my-3 mb-5" /> */}

              <div className="custom-separator my-5">
                <div class="separator-end"></div><div class="separator-middle"></div><div class="separator-end right"></div>
              </div>
            </Col>

            <h3 className="mb-1">Submissions</h3>
            <p className="text-center lead mb-4">Voting will open once the Submission Phase ends.</p>
            <CardList
              cards={cards}
              onCardClick={(card) => setSelectedCard(card)}
              onVote={currentPhase === 'voting' && user ? handleVote : null}
              settings={settings}
            />
          </>
        );
      case 'voting':
        return (
          <>
            <Col lg={10}>
              <h2 className="text-center mb-1">Voting Phase</h2>
              <p className="text-center lead pb-3">Vote for your favorite cards below!</p>
            </Col>
            <CardList
              cards={cards}
              onCardClick={(card) => setSelectedCard(card)}
              onVote={currentPhase === 'voting' && user ? handleVote : null}
              settings={settings}
            />

{themeCards.length > 0 && (
              <>
                <Col lg={10}>
                  <div className="custom-separator my-5">
                    <div className="separator-end"></div>
                    <div className="separator-middle"></div>
                    <div className="separator-end right"></div>
                  </div>
                </Col>
                <Col lg={9} className="mt-1 pb-3">
                  <h2 className="text-center mb-3 pb-5">Vote for Next Week's Theme</h2>
                  <Row>
                    {themeCards.map((theme) => (
                      <Col md={4} key={theme.id} className="mb-4">
                        <div className="contest-theme-styling theme-card d-flex flex-column h-100 text-center p-3">
                          <img
                            src={`/images/contest/sketch/${theme.image ? theme.image : "2"}.svg`}
                            className="w-100"
                            alt="theme"
                          />
<hr className="" />
                          <h5 className="my-1">{theme.theme}</h5>
                          <div className="mt-auto">

                            <Button
                              variant={theme.voted ? "success" : "primary"}
                              className="w-100 mt-2"
                              onClick={() => handleThemeVote(theme.id)}
                            >
                              {theme.voted ? "Voted" : "Vote"}
                            </Button>


                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Col>
              </>
            )}

          </>
        );
      case 'cooldown':
        const maxVotes = themeCards.length > 0 ? Math.max(...themeCards.map(theme => theme.votes)) : 0;
        return (
          <>
            <Col lg={10}>
              <div className="text-center">
                <h2 className="text-center mb-1">Contest Ended</h2>
                {contestInfo.winners && contestInfo.winners.length > 0 ? (
                  <>
                    <p className="text-center lead pb-3"><strong>Participants</strong>: {contestInfo.stats.participants}, <strong>Eligible Voters:</strong> {contestInfo.stats.voter_count}, <strong>Avg Votes/Voter:</strong> {contestInfo.stats.avg_votes_per_voter}</p>


                    <div className="d-flex flex-wrap justify-content-center">
                      {contestInfo.winners.map((winner) => (
                        <div key={winner.id} className="m-3">
                          <div className="row">
                            {/* Winning Card */}
                            <div className="col-md-6 text-center">
                              <h5>Winner Card</h5>
                              <Link
                                to={buildUrlWithParams({
                                  ...Object.fromEntries(new URLSearchParams(window.location.search)),
                                  selectedCard: winner.cardId
                                })}
                                preventScrollReset={true}
                                style={{ textDecoration: 'none', zIndex: '2' }}
                              >
                                <img
                                  src={`/cards/${winner.cardId2}.webp`}
                                  alt={`Winning card of ${winner.username}`}
                                  className="submission-card img-fluid mt-3"
                                  style={{ maxWidth: '300px' }}
                                />
                              </Link>
                              <p><Link to={`/user/${winner.id}`}>{winner.username}</Link>                              </p>
                            </div>
                            {/* Inline List of Voters for this Winner */}
                            <div className="col-md-6">
                              <h5>Winner Card Voters</h5>
                              {winner.voters && winner.voters.length > 0 ? (
                                <ul className="list-inline">
                                  {winner.voters.map((voter) => (
                                    <li key={voter.id} className="list-inline-item">
                                      <Link to={`/user/${voter.id}`}>
                                        {voter.username}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p>No eligible votes.</p>
                              )}

                              {/* Contest Stats Section */}
                              {contestInfo.stats && (
                                <div className="stats-section mt-5 text-center">
                                  <h5>All Eligible Voters</h5>
                                  {contestInfo.stats.voters && contestInfo.stats.voters.length > 0 ? (
                                    <ul className="list-inline">
                                      {contestInfo.stats.voters.map((voter) => (
                                        <li key={voter.id} className="list-inline-item">
                                          <Link to={`/user/${voter.id}`}>{voter.username}</Link>
                                        </li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <p>No eligible voters.</p>
                                  )}
                                </div>
                              )}

                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <Alert variant="info" className="d-inline-block">
                    <p className="mb-0">No winner has been announced.</p>
                  </Alert>
                )}
              </div>




              <div className="custom-separator my-5">
                <div className="separator-end"></div>
                <div className="separator-middle"></div>
                <div className="separator-end right"></div>
              </div>

            </Col>

            <h3 className="mb-1">All Submissions</h3>
            <p className="text-center lead pb-3">Presented in order of highest to lowest rating</p>
            <CardList
              cards={cards}
              onCardClick={(card) => setSelectedCard(card)}
              onVote={currentPhase === 'voting' && user ? handleVote : null}
              settings={settings}
            />


    {themeCards.length > 0 && (
      <>
        <Col lg={10}>
          <div className="custom-separator my-5">
            <div className="separator-end"></div>
            <div className="separator-middle"></div>
            <div className="separator-end right"></div>
          </div>
        </Col>
        <Col lg={9} className="mt-1 pb-3">
          <h2 className="text-center mb-3">Next Week's Contest</h2>
          <Row>
            {themeCards.map((theme) => (
              <Col md={4} key={theme.id} className="mb-4">
                <div
                  className={`contest-theme-styling theme-card d-flex flex-column h-100 text-center p-3 ${
                    theme.votes === maxVotes ? 'contest-theme-styling-winner' : ''
                  }`}
                >
                  <img
                    src={`/images/contest/sketch/${theme.image ? theme.image : "2"}.svg`}
                    className="w-100"
                    alt="theme"
                  />
                  <hr />
                  <h5 className="my-1">{theme.theme}</h5>
                  <div className="mt-auto">
                    <p className="m-0">Votes: {theme.votes}</p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Col>
      </>
    )}

          </>
        );


      default:
        return null;
    }
  };

  if (!contestInfo) {
    return <div>Loading...</div>;
  }

  if (currentPhase === 'not_started' || contestInfo.start_date === 0) {
    return (
      <Container className="mt-4">
        <Row className="justify-content-center">
          <Col lg={10}>
            <Row className="align-items-center mb-4">
              <Col md={4} className="text-center">
                <img
                  src={`/images/contest/sketch/${contestInfo.id}.svg`}
                  alt="Contest"
                  className="img-fluid mb-md-0 mb-2 mt-4 mt-md-1"
                  style={{ padding: '5px' }}
                />
              </Col>
              <Col md={8}>
                <h4 className="text-center text-md-start">YOU HAVE AWAKENED ME TOO SOON, EXECUTUS!</h4>
                <p className="text-muted text-md-start">{randomMessage}</p>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    );
  }


  return (
    <Container className="mt-4">
      <Row className="justify-content-center">
        <Col lg={10}>
          <h1 className="text-center mb-1">{contestInfo.title}</h1>
          {contestInfo.idea && (
            <p className="text-center text-muted mb-4">
              Idea by <Link to={`/user/${contestInfo.idea.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>{contestInfo.idea.username}</Link>
            </p>
          )}
          <Row className="align-items-center mb-4">
            <Col md={4} className="text-center">
              <img
                src={`/images/contest/sketch/${contestInfo.id}.svg`}
                alt="Contest"
                className="img-fluid mb-md-0 mb-2 mt-4 mt-md-1"
                style={{ padding: '5px' }}
              />
            </Col>
            <Col md={8}>
              <ContestPhaseIndicator
                phase={currentPhase}
                startDate={contestInfo.start_date}
                submissionsEndDate={contestInfo.submissions_end_date}
                endDate={contestInfo.end_date}
              />
              <p className="lead text-center text-md-start" dangerouslySetInnerHTML={{ __html: contestInfo.description }}></p>
            </Col>
          </Row>

          {/* <hr className="my-5" style={{ borderWidth: '3px' }} /> */}
          {/* <img src="https://www.hearthcards.net/images/contest/separator.webp" className="img-fluid my-3 mb-5" /> */}
          <div className="custom-separator my-5">
            <div class="separator-end"></div><div class="separator-middle"></div><div class="separator-end right"></div>
          </div>
        </Col>

        {renderPhaseContent()}

        <p className="mb-5"></p>

      </Row>

      {selectedCard && (
        <CardModal
          card={selectedCard.id}
          onClose={() => setSelectedCard(null)}
          show={!!selectedCard}
        />
      )}
      {showCardPicker && (
        <CardPicker
          type="contest"
          onConfirm={(selectedCards) => {
            if (selectedCards.length > 0) {
              handleSubmit(selectedCards[0]);
            }
            setShowCardPicker(false);
          }}
          onClose={() => setShowCardPicker(false)}
          limit={1}
        />
      )}
    </Container>
  );
};

export default Contest;